import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { modelProfilesQueryKey } from "@/shared/domain/agentThread/hooks/useModelProfiles";

import type {
  BulkOrgModelConfigRequest,
  BulkOrgModelConfigResponse,
} from "../OrgLlmProviderRecord";

import { orgQueryKeys } from "./orgQueryKeys";

/**
 * Apply one model-configuration action to many orgs (Roboto admins). A
 * `dry_run` request writes nothing and refreshes nothing; a real one
 * refetches every model-configuration search, every org's cached row, and
 * every org's model profiles.
 */
export function useBulkUpdateOrgModelConfigs() {
  const { orgService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    BulkOrgModelConfigResponse,
    Error,
    BulkOrgModelConfigRequest
  >({
    mutationFn: (request) => orgService.bulkUpdateOrgModelConfigs(request),
    onSuccess: (_result, request) => {
      if (request.dry_run === true) {
        return;
      }
      invalidateModelConfigCaches(queryClient, request.org_ids);
    },
  });
}

/** Refetch everything that shows the model configuration of `orgIds`. */
function invalidateModelConfigCaches(
  queryClient: QueryClient,
  orgIds: readonly string[],
) {
  void queryClient.invalidateQueries({
    queryKey: orgQueryKeys.modelConfigSearches(),
  });
  void queryClient.invalidateQueries({ queryKey: modelProfilesQueryKey() });
  for (const orgId of orgIds) {
    void queryClient.invalidateQueries({
      queryKey: orgQueryKeys.llmProvider(orgId),
    });
  }
}
